import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { useAuthStore } from "../../stores/authStore";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import BgLogin from "../../images/BgLogin.png";

// Validation schema
const schema = yup.object().shape({
  name: yup
    .string()
    .min(2, "Name must be at least 2 characters")
    .required("Full name is required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Password confirmation is required"),
  role: yup
    .string()
    .oneOf(["resident", "barangay_official"], "Invalid role selected")
    .required("Role is required"),
  phone_number: yup.string().nullable(),
});

/**
 * Register Page Component
 *
 * Handles user registration
 */
function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const { register: registerUser, isLoading, error } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      role: "resident",
    },
  });

  const onSubmit = async (data) => {
    try {
      await registerUser(data);
      toast.success("Registration successful!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.message || "Registration failed");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${BgLogin})` }}
    >
      <div className="absolute inset-0 bg-black/40"></div>
      <div className="z-10 w-full max-w-3xl bg-white rounded-[32px] shadow-2xl p-8 md:p-12">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-[#7c5cfc] text-white font-bold p-2 rounded-lg text-sm w-10 h-10 flex items-center justify-center">
              B360
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800 leading-none">
                Barangay 360
              </h1>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                Management System
              </p>
            </div>
          </div>
          <Link
            to="/"
            className="text-blue-600 font-semibold text-sm hover:underline"
          >
            Cancel
          </Link>
        </div>

        <div className="mb-8">
          <h2 className="text-3xl font-extrabold text-[#2563eb] mb-2">
            Create an Account
          </h2>
          <p className="text-gray-500 text-sm">
            Create an account to get started with barangay services.
          </p>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Account Type (Select) */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Account Type
            </label>
            <select
              {...register("role")}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 1rem center",
                backgroundSize: "1em",
              }}
            >
              <option value="resident">Resident</option>
              <option value="barangay_official">Barangay Official</option>
            </select>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">
              Full Name
            </label>
            <input
              {...register("name")}
              type="text"
              placeholder="Enter full name"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
            {errors.name && (
              <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Email and Phone Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Email Address
              </label>
              <input
                {...register("email")}
                type="email"
                placeholder="Enter email address"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Phone Number (Optional)
              </label>
              <input
                {...register("phone_number")}
                type="tel"
                placeholder="Enter phone number"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          {/* Passwords Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Password
              </label>
              <input
                {...register("password")}
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] text-gray-400"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                {...register("password_confirmation")}
                type={showPasswordConfirm ? "text" : "password"}
                placeholder="Confirm your password"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-3 top-[38px] text-gray-400"
              >
                {showPasswordConfirm ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
              {errors.password_confirmation && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.password_confirmation.message}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#2563eb] hover:bg-blue-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] disabled:bg-blue-300"
          >
            {isLoading ? "Creating account..." : "Next"}
          </button>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500 font-medium">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-blue-600 font-bold hover:underline"
              >
                Log in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterPage;
